/**
 * Small focused dialog for bulk-assigning 分類 (mfg_item_categories) to N
 * items on /mfg/inventory. Mirrors the pattern of shizai/CategoryEditDialog
 * so the UX is identical between REQs and 品目マスター.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { mfgApi } from './mfgApi';

const NONE_VAL = '__none__';

export default function ItemCategoryBulkDialog({ open, itemIds, hint, onClose, onSaved }) {
  const [categories, setCategories] = useState([]);
  const [pick, setPick] = useState(NONE_VAL);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    mfgApi.itemCategories().then((list) => setCategories(list || [])).catch(() => setCategories([]));
    setPick(NONE_VAL);
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const value = pick === NONE_VAL ? null : pick;
      const res = await mfgApi.bulkUpdateItemCategory(itemIds, value);
      toast.success(`${res.modified}件の分類を更新しました`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '分類の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="item-category-bulk-dialog">
        <DialogHeader>
          <DialogTitle>分類を一括設定</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 px-3 py-2">
            <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">対象</div>
            <div className="mt-0.5 text-sm font-semibold text-zinc-800">{itemIds.length}件の品目</div>
            {hint && (
              <div className="text-xs text-zinc-500 truncate" title={hint}>{hint}</div>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">分類</Label>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="item-category-bulk-select" className="h-10 mt-1">
                <SelectValue placeholder="分類を選択" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VAL}>なし</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                    {typeof c.item_count === 'number' && (
                      <span className="text-zinc-400 ml-1.5">· {c.item_count}件</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-zinc-400 mt-1">
              選択した品目すべてに同じ分類が設定されます。既に別の分類が設定されていた場合は上書きされます。
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="item-category-bulk-save"
          >
            {saving ? '保存中...' : `${itemIds.length}件を更新`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
